import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { APP_STYLE_PALETTE } from '../../../styles/msiStyleTokens';

export const DASHBOARD_PAGE_PRESENTATION = Object.freeze({
  overview: Object.freeze({
    title: 'Risk Overview',
    subtitle: 'Portfolio risk, factor exposures and key metrics at a glance.',
  }),
  watchlist: Object.freeze({
    title: 'Watchlist',
    subtitle: 'Track names, compare risk metrics, and prepare candidates for optimization.',
  }),
  'pos-sizing': Object.freeze({
    title: 'Position Sizing',
    subtitle: 'Size positions, review trade ideas, and monitor exposure targets.',
  }),
  optimizer: Object.freeze({
    title: 'Optimizer',
    subtitle: 'Optimize your portfolio by adjusting position weights subject to risk and return constraints.',
  }),
  performance: Object.freeze({
    title: 'Performance',
    subtitle: 'Review portfolio results and compare saved optimization scenarios.',
  }),
});

export const ADMIN_PAGE_PRESENTATION = Object.freeze({
  'factor-data': Object.freeze({
    title: 'Factor Data',
    subtitle: 'Publish, inspect, and monitor the factor data served throughout Alphamizer.',
  }),
  'security-identity': Object.freeze({
    title: 'Security Identity',
    subtitle: 'Inspect identity intake, resolution, conflicts, securities, and immutable deployments.',
  }),
  'mosek-license': Object.freeze({
    title: 'MOSEK Licence',
    subtitle: "Check, upload and apply this environment's optimizer licence.",
  }),
  'market-data': Object.freeze({
    title: 'Market Data',
    subtitle: 'Watch the market-data checks behind every Watchlist and the securities that need attention.',
  }),
});

const APP_BAR_PAGE_PRESENTATION = Object.freeze({
  ...DASHBOARD_PAGE_PRESENTATION,
  ...ADMIN_PAGE_PRESENTATION,
});

export const DASHBOARD_VIEW_ROUTES = Object.freeze(Object.keys(DASHBOARD_PAGE_PRESENTATION));

export const resolveDashboardActiveView = (pathname) => {
  const pathParts = String(pathname || '').split('/').filter(Boolean);
  const route = pathParts[0] === 'dashboard'
    ? pathParts[1] || 'overview'
    : pathParts.length === 1 ? pathParts[0] : null;
  return DASHBOARD_VIEW_ROUTES.includes(route) ? route : null;
};

export const resolveAppBarActiveView = (pathname) => {
  const pathParts = String(pathname || '').split('/').filter(Boolean);
  if (pathParts[0] === 'admin' && ADMIN_PAGE_PRESENTATION[pathParts[1]]) {
    return pathParts[1];
  }
  return resolveDashboardActiveView(pathname);
};

// Sizing for the app bar slot that holds this header. Dashboard pages keep a
// clamped slot beside their toolbar actions; admin workspaces render their
// tabs in the page body, so the header may take its full natural width (and
// still truncates with an ellipsis when the bar is narrow).
export const appBarHeaderSlotSx = (activeView) => {
  if (!activeView) return { flex: 1, minWidth: 0, height: '100%', overflow: 'hidden' };
  if (ADMIN_PAGE_PRESENTATION[activeView]) {
    return { flex: '0 1 auto', minWidth: { xs: 100, md: 155 }, height: '100%', overflow: 'hidden' };
  }
  return {
    flex: '0 1 clamp(170px, 22vw, 330px)',
    minWidth: { xs: 100, md: 155 },
    height: '100%',
    overflow: 'hidden',
  };
};

export default function DashboardPageHeader({ activeView, actions = null }) {
  const presentation = APP_BAR_PAGE_PRESENTATION[activeView]
    || DASHBOARD_PAGE_PRESENTATION.overview;

  return (
    <Box
      component="header"
      sx={{
        minWidth: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
        <Typography
          component="h1"
          noWrap
          sx={{
            color: APP_STYLE_PALETTE.ink900,
            fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.12rem', lg: '1.22rem' },
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {presentation.title}
        </Typography>
        <Typography
          noWrap
          sx={{
            display: { xs: 'none', md: 'block' },
            mt: 0.2,
            color: APP_STYLE_PALETTE.ink500,
            fontSize: { md: '0.68rem', lg: '0.72rem' },
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {presentation.subtitle}
        </Typography>
      </Box>
      {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
    </Box>
  );
}

DashboardPageHeader.propTypes = {
  activeView: PropTypes.string.isRequired,
  actions: PropTypes.node,
};
